import React, { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "../lib/api";
import { Card, Button } from "./ui";
import { Languages, Check, X, RefreshCw, Loader2, AlertTriangle, BarChart3, DollarSign, Bell, Save } from "lucide-react";
import { LANGS } from "../i18n";

const SURFACES = [
  ["ui_phrases", "UI"],
  ["landing", "Landing"],
  ["solutions", "Solutions"],
  ["tours_sandbox", "Tours/Sandbox"],
  ["runner_manual", "Runner manual"],
  ["cs_frontend_manual", "CS manual"],
];

const langLabel = (code) => {
  const l = LANGS.find((x) => x.code === code);
  return l ? l.label : code;
};
const nfmt = (n) => (n || 0).toLocaleString();

/**
 * Inline SVG sparkline for the 30-day tokens/day series.
 * Draws a filled area + a thin line + a red-dashed threshold line if one is set.
 */
function Sparkline({ daily, threshold }) {
  const W = 480, H = 60, PAD = 4;
  const values = (daily || []).map((d) => d.tokens || 0);
  const maxVal = Math.max(1, threshold || 0, ...values);
  const n = values.length || 1;
  const dx = (W - PAD * 2) / Math.max(1, n - 1);
  const y = (v) => H - PAD - ((v / maxVal) * (H - PAD * 2));
  const points = values.map((v, i) => [PAD + i * dx, y(v)]);
  const linePath = points.map(([x, yy], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${yy.toFixed(1)}`).join(" ");
  const areaPath = points.length
    ? `${linePath} L${points[points.length - 1][0].toFixed(1)},${H - PAD} L${PAD},${H - PAD} Z`
    : "";
  const thY = threshold ? y(threshold) : null;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="60" data-testid="th-cost-sparkline" preserveAspectRatio="none">
      <defs>
        <linearGradient id="sparkGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {areaPath ? <path d={areaPath} fill="url(#sparkGrad)" /> : null}
      {linePath ? <path d={linePath} fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" /> : null}
      {points.map(([x, yy], i) => (values[i] > 0 ? <circle key={i} cx={x} cy={yy} r="2" fill="var(--primary)" /> : null))}
      {thY !== null && threshold > 0 ? (
        <g>
          <line x1={PAD} x2={W - PAD} y1={thY} y2={thY} stroke="#DA1E28" strokeWidth="1" strokeDasharray="4,3" />
          <text x={W - PAD - 2} y={thY - 2} fill="#DA1E28" fontSize="9" textAnchor="end">threshold {nfmt(threshold)}</text>
        </g>
      ) : null}
    </svg>
  );
}

export default function TranslationHealth() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [cost, setCost] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savingBudget, setSavingBudget] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ threshold_tokens: "", email: "" });

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [h, a, c] = await Promise.all([
        client.get("/admin/translation-health"),
        client.get("/admin/lang-analytics").catch(() => ({ data: null })),
        client.get("/admin/warm-cost-ledger?limit=10").catch(() => ({ data: null })),
      ]);
      setData(h.data);
      setAnalytics(a.data);
      setCost(c.data);
      if (c.data && c.data.budget) {
        setBudgetForm({
          threshold_tokens: c.data.budget.threshold_tokens ? String(c.data.budget.threshold_tokens) : "",
          email: c.data.budget.email || "",
        });
      }
    } catch (e) { setErr(apiErr(e)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      const t = parseInt(budgetForm.threshold_tokens || "0", 10) || 0;
      const { data: updated } = await client.put("/admin/warm-cost-budget", {
        threshold_tokens: t, email: budgetForm.email || "",
      });
      setCost((c) => c ? { ...c, budget: { ...updated } } : c);
    } catch (e) { setErr(apiErr(e)); }
    finally { setSavingBudget(false); }
  };

  const Dot = ({ ok, title }) => (
    <span title={title} className={`inline-flex h-4 w-4 items-center justify-center rounded-full ${ok ? "bg-[#24A148]/20 text-[#24A148]" : "bg-[#DA1E28]/20 text-[#DA1E28]"}`} data-testid={`th-dot-${title}`}>
      {ok ? <Check size={11} /> : <X size={11} />}
    </span>
  );

  const maxPicks = analytics && analytics.top && analytics.top.length ? analytics.top[0].picks : 0;

  return (
    <Card className="p-4" data-testid="translation-health">
      <div className="mb-3 flex items-center gap-2">
        <Languages size={18} className="text-[var(--primary)]" />
        <h3 className="text-sm font-semibold">Translation health</h3>
        {data ? (
          <span className={`ms-2 rounded-full px-2 py-0.5 text-[11px] font-medium ${data.healthy ? "bg-[#24A148]/15 text-[#24A148]" : "bg-[#F1620E]/15 text-[#F1620E]"}`} data-testid="th-overall">
            {data.healthy ? "All languages healthy" : "Gaps found"}
          </span>
        ) : null}
        <Button variant="ghost" className="ms-auto text-xs" onClick={load} disabled={busy} data-testid="th-refresh">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Re-check
        </Button>
      </div>
      <p className="mb-3 text-xs opacity-60">Pings every localized surface for the 14 non-English languages and flags anything that would fall back to English.</p>
      {err ? <p className="text-xs text-[#DA1E28]" data-testid="th-error">{err}</p> : null}
      {data ? (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-start opacity-60">
                  <th className="py-1 pe-2 text-start">Lang</th>
                  {SURFACES.map(([k, label]) => <th key={k} className="px-2 py-1 font-medium">{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.languages.map((r) => (
                  <tr key={r.lang} className="border-t border-black/5 dark:border-white/5" data-testid={`th-row-${r.lang}`}>
                    <td className="py-1 pe-2 font-mono font-semibold">{r.lang}</td>
                    {SURFACES.map(([k]) => (
                      <td key={k} className="px-2 py-1 text-center">
                        <Dot ok={r[k].ok} title={`${r.lang}-${k}`} />
                        {k === "ui_phrases" && r[k].detail ? <div className="mt-0.5 text-[9px] opacity-50">{r[k].detail}</div> : null}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 rounded-md bg-black/[0.03] px-3 py-2 text-xs dark:bg-white/[0.05]" data-testid="th-unharvested">
            <span className="font-medium">Unharvested UI phrases (leak English everywhere): </span>
            <span className={data.unharvested_ui_phrases.count ? "text-[#DA1E28] font-semibold" : "text-[#24A148] font-semibold"}>
              {data.unharvested_ui_phrases.count}
            </span>
            {data.unharvested_ui_phrases.count ? (
              <ul className="mt-1 list-disc ps-5 opacity-70">
                {data.unharvested_ui_phrases.sample.map((s, i) => <li key={i} className="truncate">{s}</li>)}
              </ul>
            ) : null}
          </div>
          {data.landing_en_gap ? (
            <div className={`mt-2 rounded-md px-3 py-2 text-xs ${data.landing_en_gap.count ? "bg-[#DA1E28]/10" : "bg-black/[0.03] dark:bg-white/[0.05]"}`} data-testid="th-landing-gap">
              <div className="flex items-center gap-1">
                {data.landing_en_gap.count ? <AlertTriangle size={12} className="text-[#DA1E28]" /> : null}
                <span className="font-medium">Marketing.js LANDING_EN keys missing from backend LANDING_STRINGS: </span>
                <span className={data.landing_en_gap.count ? "text-[#DA1E28] font-semibold" : "text-[#24A148] font-semibold"}>
                  {data.landing_en_gap.count}
                </span>
              </div>
              {data.landing_en_gap.count ? (
                <>
                  <p className="mt-0.5 opacity-70">These keys render English on every non-English Solutions/Pricing page. Add them to <code className="rounded bg-black/10 px-1 dark:bg-white/10">LANDING_STRINGS</code> in <code>routes/marketing_site.py</code>.</p>
                  <ul className="mt-1 list-disc ps-5 opacity-80">
                    {data.landing_en_gap.sample.map((s, i) => <li key={i}><code className="text-[10px]">{s}</code></li>)}
                  </ul>
                </>
              ) : null}
            </div>
          ) : null}
          <p className="mt-2 text-[10px] opacity-40">Checked {new Date(data.checked_at).toLocaleString()}</p>
        </>
      ) : (!err ? <p className="text-xs opacity-50" data-testid="th-loading">Loading…</p> : null)}

      {analytics ? (
        <div className="mt-4 border-t border-black/10 pt-4 dark:border-white/10" data-testid="th-analytics">
          <div className="mb-2 flex items-center gap-2">
            <BarChart3 size={16} className="text-[var(--primary)]" />
            <h4 className="text-sm font-semibold">Language picks · last 30 days</h4>
            <span className="ms-auto text-[11px] opacity-60" data-testid="th-analytics-total">{analytics.total_events_30d} events</span>
          </div>
          {analytics.total_events_30d === 0 ? (
            <p className="text-xs opacity-60">No language switches recorded yet — visitors will start populating this once they use the picker.</p>
          ) : (
            <div className="space-y-1">
              {analytics.languages.filter((r) => r.picks > 0).slice(0, 10).map((r) => (
                <div key={r.lang} className="flex items-center gap-2 text-xs" data-testid={`th-analytics-row-${r.lang}`}>
                  <span className="w-24 shrink-0 truncate font-medium">{langLabel(r.lang)}</span>
                  <span className="w-8 shrink-0 font-mono text-[10px] opacity-60">{r.lang}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
                    <div className="h-full rounded-full bg-[var(--primary)]" style={{ width: `${maxPicks ? (r.picks / maxPicks) * 100 : 0}%` }} />
                  </div>
                  <span className="w-14 shrink-0 text-end tabular-nums">{r.picks}</span>
                  <span className="w-12 shrink-0 text-end text-[10px] tabular-nums opacity-60">{r.unique}u</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {cost ? (
        <div className="mt-4 border-t border-black/10 pt-4 dark:border-white/10" data-testid="th-cost">
          <div className="mb-2 flex items-center gap-2">
            <DollarSign size={16} className="text-[var(--primary)]" />
            <h4 className="text-sm font-semibold">Translation cost · recent auto-warms</h4>
            {cost.budget && cost.budget.enabled ? (
              <span className="ms-1 rounded-full bg-[#F1620E]/15 px-2 py-0.5 text-[10px] font-medium text-[#F1620E]" data-testid="th-cost-budget-badge">
                Alert &gt; {nfmt(cost.budget.threshold_tokens)} tokens
              </span>
            ) : null}
          </div>
          <div className="mb-2 grid grid-cols-4 gap-2 text-[11px]">
            <div className="rounded-md bg-black/[0.03] p-2 dark:bg-white/[0.05]" data-testid="th-cost-total-sessions">
              <div className="opacity-60">Sessions</div>
              <div className="text-sm font-semibold tabular-nums">{nfmt(cost.totals.sessions)}</div>
            </div>
            <div className="rounded-md bg-black/[0.03] p-2 dark:bg-white/[0.05]" data-testid="th-cost-total-calls">
              <div className="opacity-60">LLM calls</div>
              <div className="text-sm font-semibold tabular-nums">{nfmt(cost.totals.calls)}</div>
            </div>
            <div className="rounded-md bg-black/[0.03] p-2 dark:bg-white/[0.05]" data-testid="th-cost-total-chars">
              <div className="opacity-60">Characters</div>
              <div className="text-sm font-semibold tabular-nums">{nfmt(cost.totals.chars_in + cost.totals.chars_out)}</div>
            </div>
            <div className="rounded-md bg-[var(--primary)]/10 p-2" data-testid="th-cost-total-tokens">
              <div className="opacity-60">Est. tokens</div>
              <div className="text-sm font-semibold tabular-nums text-[var(--primary)]">{nfmt(cost.totals.est_tokens)}</div>
            </div>
          </div>
          {cost.daily && cost.daily.length ? (
            <div className="mb-3 rounded-md bg-black/[0.03] p-2 dark:bg-white/[0.05]">
              <div className="mb-1 flex items-center justify-between text-[10px] opacity-60">
                <span>Tokens burned per day · last 30 days</span>
                <span>{cost.daily[0].day} → {cost.daily[cost.daily.length - 1].day}</span>
              </div>
              <Sparkline daily={cost.daily} threshold={cost.budget && cost.budget.threshold_tokens} />
              <div className="mt-1 flex items-center justify-between text-[10px] opacity-50">
                <span>0</span>
                <span>{nfmt(Math.max(...cost.daily.map((d) => d.tokens || 0), (cost.budget && cost.budget.threshold_tokens) || 0))}</span>
              </div>
            </div>
          ) : null}
          {cost.rows.length === 0 ? (
            <p className="text-xs opacity-60">No auto-warm sessions recorded yet — a row lands here whenever the marketing copy hash changes on boot.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-start opacity-60">
                    <th className="py-1 pe-2 text-start">When</th>
                    <th className="px-2 py-1 text-start">Event</th>
                    <th className="px-2 py-1 text-end">Langs</th>
                    <th className="px-2 py-1 text-end">Calls</th>
                    <th className="px-2 py-1 text-end">Chars</th>
                    <th className="px-2 py-1 text-end">~Tokens</th>
                    <th className="px-2 py-1 text-end">Elapsed</th>
                  </tr>
                </thead>
                <tbody>
                  {cost.rows.map((r, i) => (
                    <tr key={i} className="border-t border-black/5 dark:border-white/5" data-testid={`th-cost-row-${i}`}>
                      <td className="py-1 pe-2 whitespace-nowrap">{new Date(r.at).toLocaleString()}</td>
                      <td className="px-2 py-1 font-mono text-[10px]">{r.event}{r.alerted ? <span className="ms-1 text-[9px] text-[#F1620E]">⚑ alerted</span> : null}</td>
                      <td className="px-2 py-1 text-end tabular-nums">{(r.warmed_landing || 0) + (r.warmed_solutions || 0)}</td>
                      <td className="px-2 py-1 text-end tabular-nums">{nfmt(r.calls)}</td>
                      <td className="px-2 py-1 text-end tabular-nums">{nfmt((r.chars_in || 0) + (r.chars_out || 0))}</td>
                      <td className={`px-2 py-1 text-end font-semibold tabular-nums ${cost.budget && cost.budget.enabled && r.est_tokens >= cost.budget.threshold_tokens ? "text-[#DA1E28]" : "text-[var(--primary)]"}`}>{nfmt(r.est_tokens)}</td>
                      <td className="px-2 py-1 text-end tabular-nums opacity-60">{Math.round((r.elapsed_ms || 0) / 100) / 10}s</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-2 text-[10px] opacity-40">Tokens estimated as (chars_in + chars_out) ÷ 4 — English baseline; CJK/RTL glyphs may over-estimate.</p>

          <div className="mt-3 rounded-md border border-dashed border-black/10 p-2 text-[11px] dark:border-white/10" data-testid="th-cost-budget-form">
            <div className="mb-1 flex items-center gap-1 font-medium">
              <Bell size={12} className="text-[var(--primary)]" />
              Cost budget alert
              <span className="opacity-60 font-normal">— email me if one warm session burns more than</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="number"
                min="0"
                step="1000"
                placeholder="e.g. 50000"
                value={budgetForm.threshold_tokens}
                onChange={(e) => setBudgetForm((f) => ({ ...f, threshold_tokens: e.target.value }))}
                className="w-32 rounded-md border border-black/10 bg-white px-2 py-1 text-xs dark:border-white/10 dark:bg-black/40"
                data-testid="th-cost-budget-threshold"
              />
              <span className="opacity-60">tokens · email</span>
              <input
                type="email"
                placeholder="admin@example.com"
                value={budgetForm.email}
                onChange={(e) => setBudgetForm((f) => ({ ...f, email: e.target.value }))}
                className="flex-1 min-w-[180px] rounded-md border border-black/10 bg-white px-2 py-1 text-xs dark:border-white/10 dark:bg-black/40"
                data-testid="th-cost-budget-email"
              />
              <Button variant="ghost" onClick={saveBudget} disabled={savingBudget} className="text-xs" data-testid="th-cost-budget-save">
                {savingBudget ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
              </Button>
            </div>
            <p className="mt-1 text-[10px] opacity-50">Set threshold to 0 to disable the alert. Uses your existing outbound-email provider.</p>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
